import classNames from 'classnames'
import { BaseDialogProps } from 'components/BaseDialog/BaseDialog.component'
import BaseTooltip from 'components/BaseTooltip'
import CloseIcon from 'mdi-react/CloseIcon'
import React from 'react'
import { compose } from 'recompose'

import { Dialog, DialogContent, Divider, IconButton, Theme, useMediaQuery, useTheme } from '@mui/material'
import { createStyles, withStyles, WithStyles } from '@mui/styles'

const BaseModal: React.SFC<FullBaseModalProps> = ({
  classes,
  className,
  open = true,
  title,
  children,
  footer,
  unstructured = false,
  onClick,
  onClose,
  ...otherProps
}) => {
  const theme = useTheme()

  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  return (
    <Dialog
      open={open}
      fullScreen={fullScreen}
      className={classNames(classes.modalRoot)}
      classes={{
        paper: unstructured ? classNames(className, classes.noPaperRoot) : classNames(className, classes.paperRoot),
      }}
      onClick={onClick}
      onClose={onClose}
      scroll="paper"
      disableAutoFocus
      disableEnforceFocus
      {...otherProps}
    >
      {unstructured ? (
        children
      ) : (
        <>
          <div>
            <div className={classes.headerContainer}>
              {title ? <div>{title}</div> : null}
              <BaseTooltip title="Close" bottom>
                <IconButton aria-label="Close modal" onClick={onClose} className={classes.closeButton}>
                  <CloseIcon className={classes.closeIcon} />
                </IconButton>
              </BaseTooltip>
            </div>
          </div>
          <Divider />

          <DialogContent className={classes.content}>{children}</DialogContent>

          {footer ? (
            <>
              <Divider />
              <div className={classes.footerContainer}>{footer}</div>
            </>
          ) : null}
        </>
      )}
    </Dialog>
  )
}

type FullBaseModalProps = BaseModalProps & WithStyles<typeof styles>

export type BaseModalProps = Pick<BaseDialogProps, 'fullWidth' | 'maxWidth'> & {
  className?: string
  open?: boolean
  title?: React.ReactNode
  footer?: React.ReactNode
  unstructured?: boolean
  onClick?: () => any
  onClose?: () => any
}

const styles = ({ spacing, breakpoints, palette }: Theme) => {
  const rootProps = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'auto',
    width: '100vw',
    height: '100vh',
    [breakpoints.down('xs')]: {
      display: 'block',
    },
  }
  return createStyles({
    modalRoot: {
      overflow: 'auto',
      [breakpoints.down('xs')]: {
        display: 'block',
      },
    },
    paperRoot: {
      position: 'relative',
      // Disable annoying effect in mobile Chrome.
      '-webkit-tap-highlight-color': 'rgba(0, 0, 0, 0)',
      height: 'auto',
      [breakpoints.down('xs')]: {
        height: '100%',
        minHeight: '100%',
        // Disable selects to ease tooltip discovery.
        userSelect: 'none',
      },
    },
    noPaperRoot: {
      ...rootProps,
      margin: 0,
      padding: spacing(3),
      background: 'none',
      boxShadow: 'none',
      maxWidth: 'none',
      [breakpoints.down('xs')]: {
        padding: spacing(),
      },
    },
    headerContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: spacing(3),
    },
    closeButton: {
      marginLeft: spacing(3),
    },
    closeIcon: {
      fill: palette.text.secondary,
    },
    content: {
      paddingTop: spacing(2),
      paddingBottom: spacing(2),
    },
    footerContainer: {
      padding: spacing(3),
    },
  })
}

export default compose<FullBaseModalProps, BaseModalProps>(withStyles(styles))(BaseModal)
